import styles from './LogInWrapper.module.css';

import Button from "@mui/material/Button";
import Alert from '@mui/material/Alert';
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { useAuth } from "../../AuthContext.jsx";
import { onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth";
import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";

const loadScript = (src) => {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${ src }"]`)) return resolve();
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Failed to load script: ' + src));
        document.head.appendChild(s);
    });
};

export const LogInWrapper = () => {
    const [ error, setError ] = useState(false);
    const [ email, setEmail ] = useState('');
    const [ user, setUser ] = useState(null);
    const [ loading, setLoading ] = useState(true);
    const [ password, setPassword ] = useState('');
    const [ recaptchaToken, setRecaptchaToken ] = useState(null);

    const { auth, signOutUser } = useAuth();

    // Use grecaptcha.execute(siteKey, { action }) (reCAPTCHA v3 style) so Google returns assessment scores.
    const getRecaptchaToken = () => {
        return new Promise((resolve, reject) => {
            const grecaptchaReady = !!window.grecaptcha && typeof window.grecaptcha.execute === 'function';

            if (!grecaptchaReady) return reject(new Error('reCAPTCHA not ready'));

            // Protect with a timeout in case the network or Google does not respond
            const timeout = setTimeout(() => {
                reject(new Error('reCAPTCHA timed out'));
            }, 10000);

            try {
                const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

                // request an assessment by passing an action; this is the v3 pattern and will produce a score
                window.grecaptcha.execute(siteKey, { action: 'login' })
                    .then((token) => {
                        clearTimeout(timeout);
                        resolve(token);
                    })
                    .catch((err) => {
                        clearTimeout(timeout);
                        reject(err);
                    });
            } catch (err) {
                clearTimeout(timeout);
                reject(err);
            }
        });
    };

    const handleOnChange = (event) => {
        switch (event.target.name) {
            case 'email':
                setEmail(event.target.value);

                break;
            case 'password':
                setPassword(event.target.value);

                break;
            default:
                break;
        }
    };

    const handleSignIn = async () => {
        if (!process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
            setError('reCAPTCHA not configured');

            return;
        }

        let token = recaptchaToken;

        if (!token) {
            try {
                token = await getRecaptchaToken();

                // store it for any future reuse in this session
                setRecaptchaToken(token);
            } catch (err) {
                console.error('reCAPTCHA error', err);

                setError('Please complete the reCAPTCHA');

                return;
            }
        }

        const resp = await fetch(`${ process.env.REACT_APP_API_ENDPOINT }/api/recaptcha`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token })
        });

        const json = await resp.json();

        if (!resp.ok || !json.success) {
            setError('reCAPTCHA verification failed');

            // clear any cached token for the next attempt
            setRecaptchaToken(null);

            return;
        };

        await signInWithEmailAndPassword(auth, email, password)
            .then(() => {
                setError(false);
            })
            .catch(async (error) => {
                if (error.code === 'auth/user-not-found') {
                    setError('Invalid email or wrong password');
                } else if (error.code === 'auth/wrong-password') {
                    setError('Invalid email or wrong password');
                } else if (error.code === 'auth/invalid-credential') {
                    setError('Invalid email or wrong password');
                } else if (error.code === 'auth/user-disabled') {
                    setError('User account disabled');
                } else {
                    setError(error.code || 'Error signing in');
                }
            });
    };

    useEffect(() => {
        const setupRecaptcha = async () => {
            if (!process.env.REACT_APP_RECAPTCHA_SITE_KEY) return;

            // Load reCAPTCHA v3 script which uses the site key in the render query param.
            await loadScript(`https://www.google.com/recaptcha/api.js?render=${ process.env.REACT_APP_RECAPTCHA_SITE_KEY }`);

            // grecaptcha.ready is provided by the v3 script; we don't render a widget for v3.
            if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
                try {
                    window.grecaptcha.ready(() => {
                        console.debug('grecaptcha.ready – v3 script loaded');
                    });
                } catch (err) {
                    console.debug('grecaptcha.ready failed', err);
                }
            } else {
                console.debug('grecaptcha not available after loadScript');
            }
        };

        setupRecaptcha();

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUser(user);
            } else if (!user) {
                signOutUser();
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    if (loading) {
        return <CircularProgress />;
    }

    // MARK: Render
    return (
        !user ?
            <div className={ styles.main }>
                <div className={ styles.login }>
                    <div className={ styles.loginInputs }>
                        <input type="email" name="email" placeholder="Email" autoComplete="email" value={ email } onChange={ handleOnChange } />
                        <input type="password" name="password" placeholder="Password" autoComplete="current-password" value={ password } onChange={ handleOnChange } />

                        { error && <Alert variant='filled' severity="error" sx={ { mb: 2 } }>{ error }</Alert> }
                    </div>

                    <div id="recaptcha-container" style={ { marginTop: 12 } } />

                    <div className={ styles.buttons }>
                        <Button variant="contained" color="primary" onClick={ handleSignIn }>sign in</Button>
                    </div>
                </div>

                <Typography variant="caption" color="textSecondary" align="center" sx={ { maxWidth: '325px', mt: 1, mb: 1 } }>
                    This site is protected by reCAPTCHA and the Google
                    <a href="https://policies.google.com/privacy">Privacy Policy</a> and
                    <a href="https://policies.google.com/terms">Terms of Service</a> apply.
                </Typography>
            </div>
            : !user.emailVerified ?
                <Navigate to="/help" />
                : <Navigate to="/dashboard" />
    );
};